import * as React from 'react';
import { Form, Input, message, Radio, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import SessionForm from '../../../../components/forms/SessionForm';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import { H1 } from '../../../../components/ui/Typography';
import { IeOutlined } from '@ant-design/icons';
import BusinessModel from '../../models/BusinessModel';
import StepperModel from '../../../../models/StepperModel';
import { WIZARD_STEPS } from '../../../wizard-steps-description';
import Step from '../../Step';
import WebsiteValidator from '../../../../helpers/antd-forms/WebsiteValidator';
import UrlValidator from '../../../../helpers/antd-forms/UrlValidator';
import {
  ValidationPhase,
  normalizeWebsiteForSubmission
} from '../../../../helpers/domain-url/DomainUrlValidation';
import { useDomainValidationMessage } from '../../../../hooks/useDomainValidationMessage';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import SocialMediaModel from '../../../SocialMedia/models/SocialMediaModel';
import { motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';

const { Text } = Typography;

const EnterCompanyDomain: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [isOkLoading, setIsOkLoading] = React.useState(false);
  const domainValidationPhaseRef = React.useRef<ValidationPhase>('typing');
  const getDomainValidationMessage = useDomainValidationMessage();

  const handleSave = React.useCallback(async (values: any) => {
    domainValidationPhaseRef.current = 'submit';
    setIsOkLoading(true);
    try {
      const normalized = normalizeWebsiteForSubmission({
        value: values.domain,
        mode:
          values.domainType === 'user_wants_website_domain'
            ? 'user_wants_website_domain'
            : 'user_has_existing_website_domain'
      });

      if (!normalized.isValid || !normalized.normalizedForSubmit) {
        const errorMessage = normalized.errorKey
          ? getDomainValidationMessage(normalized.errorKey)
          : t('Invalid URL format.');
        form.setFields([{ name: 'domain', errors: [errorMessage] }]);
        throw new Error(errorMessage);
      }

      const normalizedDomain = normalized.normalizedForSubmit;
      let companyData = BusinessModel.getSelectedBusiness() || BusinessModel.manualCompanyData;

      if (companyData) {
        companyData.website = normalizedDomain;
        companyData.domain_type = values.domainType;

        BusinessModel.businessWizardData.setFormData({
          ...companyData,
          website: normalizedDomain,
          domain_type: values.domainType,
        });
      }

      BusinessModel.setSelectedBusinessDomainType(values.domainType);

      if(values.domainType === 'user_has_existing_website_domain') {
        //fire the request to load social media profiles so that it loads by the time the business data is loaded
        // do not wait for the request to finish before proceeding to next step
        SocialMediaModel.loadSocialMediaProfiles({
          location: companyData.address,
          businessName: companyData.name,
          website: normalizedDomain || ''
        });
      }
      
      await (
        WIZARD_STEPS[StepperModel.currentStep].step as Step
      ).moveToNextStep();
    } catch (errorInfo) {
      console.log('Validation Failed:', errorInfo);
      message.error(t('Please fill in all required fields'));
    } finally {
      setIsOkLoading(false);
    }
  }, [form, getDomainValidationMessage, t]);

  const handleValuesChange = (changedValues: any) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, 'domain')) {
      domainValidationPhaseRef.current = 'typing';
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'domainType')) {
      domainValidationPhaseRef.current = 'blur';
      form.validateFields(['domain']).catch(() => {});
    }
  };

  const helpBoxTitleMessage = t('What do you need my domain for?'); 
  const helpBoxParagraphMessage = t("We use your current website to optimally adapt the new page. Do you have no, you can specify a desired domain - regardless of whether it is currently available or not.");

  React.useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(true);
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  return (
    <motion.div 
      className="flex items-start sm:items-center sm:text-center justify-start sm:justify-center flex-1"
      initial="hidden"
      animate="visible"
      variants={staggerContainerVariants}>
      <div className='w-full max-w-[800px] company-data-form'>
        <motion.div variants={fadeInUpVariants}>
          <H1>{t('Enter the domain of your page')}</H1>
        </motion.div>
        
        <SessionForm
          form={form}
          formName="enterCompanyDomainForm"
          formProps={{
            layout: 'vertical',
            onFinish: handleSave,
            onValuesChange: handleValuesChange
          }}>
          <motion.div variants={fadeInUpVariants}>
            <Form.Item
              label={<Text strong>{t('Do you already have a website?')}</Text>}
              name="domainType"
              className="mb-[36px]"
              rules={[{ required: true }]}>
              <Radio.Group className="flex flex-col sm:flex-row gap-2 sm:gap-8">
                <Radio value="user_has_existing_website_domain">
                  {t('Yes, I have a website')}
                </Radio>
                <Radio value="user_wants_website_domain">
                  {t("No, I'm creating a new website")}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </motion.div>

          <motion.div variants={fadeInUpVariants}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.domainType !== currentValues.domainType
              }>
              {({ getFieldValue }) => {
                return (
                  <Form.Item
                    label={<Text strong>{t('Your Internet address')}</Text>}
                    name="domain"
                    className="mb-8"
                    rules={[
                      { required: true, message: t('Required') },
                      // Conditionally apply WebsiteValidator or UrlValidator
                      getFieldValue('domainType') === 'user_has_existing_website_domain'
                        ? WebsiteValidator(
                            t('Please enter a valid URL format (e.g., example.com).'),
                            t('Website does not seem to exist or is unreachable.'),
                            t('Failed to validate website.'),
                            {
                              mode: 'user_has_existing_website_domain',
                              getValidationPhase: () =>
                                domainValidationPhaseRef.current
                            }
                          )
                        : UrlValidator(t('Invalid URL format.'), true, {
                            mode: 'user_wants_website_domain',
                            getValidationPhase: () =>
                              domainValidationPhaseRef.current
                          })
                    ]}
                    hasFeedback // Show loading indicator when validating
                    validateTrigger={['onChange', 'onBlur']}
                    validateDebounce={1000}>
                    <Input
                      size="large"
                      prefix={<IeOutlined />}
                      placeholder="https://www.your-website-domain.de/"
                      autoComplete="off"
                      onChange={() => {
                        domainValidationPhaseRef.current = 'typing';
                      }}
                      onBlur={() => {
                        domainValidationPhaseRef.current = 'blur';
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </motion.div>

          <motion.div variants={fadeInUpVariants}>
            <Form.Item 
              className="flex justify-center mb-0"
              shouldUpdate>
              {({ getFieldsError, isFieldValidating }) => {
                const hasErrors = getFieldsError().some(({ errors }) => errors.length);
                
                return (
                  <AntdCustomButton
                    appearance="important"
                    htmlType="submit"
                    loading={isOkLoading}
                    onClick={() => {
                      domainValidationPhaseRef.current = 'submit';
                    }}
                    disabled={hasErrors || isFieldValidating('domain')}>
                    {t('Save')}
                  </AntdCustomButton>
                );
              }}
            </Form.Item>
          </motion.div>
        </SessionForm>
      </div>
    </motion.div>
  );
});

export default EnterCompanyDomain;